import { useState, useEffect, useCallback } from 'react';
import { Send, Users, Clock, Mail, MailCheck, Calendar, BarChart3, Zap } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { logger } from '../../utils/logger';

const API = process.env.REACT_APP_BACKEND_URL;

const SEGMENTS = [
  { value: 'all', label: 'All Users' },
  { value: 'active_7d', label: 'Active Last 7 Days' },
  { value: 'inactive_7d', label: 'Inactive 7+ Days' },
  { value: 'no_feedback', label: 'No Feedback Submitted' },
  { value: 'no_consent', label: 'Consent Not Signed' },
];

export default function AdminBroadcast() {
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [segment, setSegment] = useState('all');
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const [result, setResult] = useState('');
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailAction, setEmailAction] = useState(null);
  const [emailResult, setEmailResult] = useState('');

  const fetchHistory = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/messages`, { credentials: 'include' });
      if (res.ok) setHistory((await res.json()).broadcasts || []);
    } catch (err) { logger.error("Operation failed:", err); }
  }, []);

  const fetchEmailStatus = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/email/status`, { credentials: 'include' });
      if (res.ok) setEmailStatus(await res.json());
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { fetchHistory(); fetchEmailStatus(); }, [fetchHistory, fetchEmailStatus]);

  const handleSend = async () => {
    if (!subject.trim() || !body.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/api/admin/messages`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ subject, body, segment })
      });
      const data = await res.json();
      setResult(data.message);
      setSubject(''); setBody('');
      fetchHistory();
    } catch { setResult('Failed to send'); }
    setSending(false);
  };

  const handleEmailAction = async (action) => {
    setEmailAction(action);
    setEmailResult('');
    try {
      let endpoint = '';
      if (action === 'promotion') {
        if (!subject.trim() || !body.trim()) {
          setEmailResult('Fill in subject and body first');
          setEmailAction(null);
          return;
        }
        endpoint = '/api/email/send-promotion';
        const res = await fetch(`${API}${endpoint}`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ subject, body, target: segment === 'all' ? 'all' : 'active' })
        });
        const data = await res.json();
        setEmailResult(`Sent: ${data.sent}, Failed: ${data.failed || 0}`);
      } else {
        const map = {
          daily: '/api/email/send-daily-batch',
          weekly: '/api/email/send-weekly-batch',
          monthly: '/api/email/send-monthly-batch',
        };
        endpoint = map[action];
        const res = await fetch(`${API}${endpoint}`, {
          method: 'POST', credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
        });
        const data = await res.json();
        setEmailResult(`Sent: ${data.sent}, Failed: ${data.failed || 0}, Active: ${data.total_active || data.total_users || 0}`);
      }
    } catch (e) {
      setEmailResult(`Error: ${e.message}`);
    }
    setEmailAction(null);
  };

  const handleTestEmail = async () => {
    setEmailAction('test');
    try {
      const res = await fetch(`${API}/api/email/test`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: 'admin@sendpi.ai' })
      });
      const data = await res.json();
      setEmailResult(data.sent ? 'Test email sent!' : `Failed: ${data.reason || 'Email delivery failed — verify domain in Resend'}`);
    } catch (e) {
      setEmailResult(`Error: ${e.message}`);
    }
    setEmailAction(null);
  };

  return (
    <div data-testid="admin-broadcast" className="space-y-8">
      <h1 className="text-2xl font-bold text-white" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Communications Hub</h1>

      {/* Email Status */}
      <div className={`rounded-xl p-4 flex items-center gap-3 border ${emailStatus?.configured ? 'bg-emerald-500/10 border-emerald-500/20' : 'bg-red-500/10 border-red-500/20'}`}>
        <MailCheck className={`w-5 h-5 ${emailStatus?.configured ? 'text-emerald-400' : 'text-red-400'}`} />
        <div>
          <span className={`text-sm font-medium ${emailStatus?.configured ? 'text-emerald-400' : 'text-red-400'}`}>
            {emailStatus?.configured ? 'Resend Connected' : 'Resend Not Configured'}
          </span>
          <span className="text-xs text-white/30 ml-3">Sender: {emailStatus?.sender}</span>
        </div>
      </div>

      {/* Quick Email Actions */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
          <Mail className="w-5 h-5 text-cyan-400" /> Email Actions
        </h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <button onClick={() => handleEmailAction('daily')} disabled={!!emailAction}
            className="flex flex-col items-center gap-2 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-cyan-500/30 transition-all disabled:opacity-30"
            data-testid="email-action-daily">
            <Calendar className="w-5 h-5 text-amber-400" />
            <span className="text-white text-xs font-medium">Send Daily Digest</span>
            <span className="text-white/30 text-[10px]">Active users today</span>
          </button>
          <button onClick={() => handleEmailAction('weekly')} disabled={!!emailAction}
            className="flex flex-col items-center gap-2 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-cyan-500/30 transition-all disabled:opacity-30"
            data-testid="email-action-weekly">
            <BarChart3 className="w-5 h-5 text-violet-400" />
            <span className="text-white text-xs font-medium">Send Weekly Report</span>
            <span className="text-white/30 text-[10px]">Active users (7 days)</span>
          </button>
          <button onClick={() => handleEmailAction('monthly')} disabled={!!emailAction}
            className="flex flex-col items-center gap-2 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-cyan-500/30 transition-all disabled:opacity-30"
            data-testid="email-action-monthly">
            <Zap className="w-5 h-5 text-emerald-400" />
            <span className="text-white text-xs font-medium">Send Monthly Report</span>
            <span className="text-white/30 text-[10px]">All active users</span>
          </button>
          <button onClick={handleTestEmail} disabled={!!emailAction}
            className="flex flex-col items-center gap-2 p-4 bg-white/5 rounded-xl border border-white/5 hover:border-cyan-500/30 transition-all disabled:opacity-30"
            data-testid="email-action-test">
            <MailCheck className="w-5 h-5 text-cyan-400" />
            <span className="text-white text-xs font-medium">Send Test Email</span>
            <span className="text-white/30 text-[10px]">To admin@sendpi.ai</span>
          </button>
        </div>
        {emailAction && <p className="text-cyan-400 text-sm mt-3 animate-pulse">Sending {emailAction} emails...</p>}
        {emailResult && <p className="text-emerald-400 text-sm mt-3">{emailResult}</p>}
      </div>

      {/* Compose Broadcast / Promotion Email */}
      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6 space-y-4">
        <h2 className="text-lg font-semibold text-white mb-2">Compose Message</h2>
        <div className="flex gap-4">
          <div className="flex-1">
            <label className="text-sm text-white/50 mb-1 block">Subject</label>
            <input value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="Email subject line"
              className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-2.5 text-white text-sm focus:outline-none focus:border-cyan-500/50" />
          </div>
          <div className="w-56">
            <label className="text-sm text-white/50 mb-1 block">Segment</label>
            <select value={segment} onChange={(e) => setSegment(e.target.value)}
              className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-white text-sm focus:outline-none">
              {SEGMENTS.map(s => <option key={s.value} value={s.value} className="bg-[#0E1830]">{s.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <label className="text-sm text-white/50 mb-1 block">Message Body</label>
          <textarea value={body} onChange={(e) => setBody(e.target.value)} placeholder="Write your message... (HTML supported for email)"
            rows={5} className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-white text-sm resize-none focus:outline-none focus:border-cyan-500/50" />
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          <span className="text-sm text-white/30">From: {emailStatus?.sender || 'support@sendipi.ai'}</span>
          <div className="ml-auto flex gap-2">
            <button onClick={() => handleEmailAction('promotion')} disabled={sending || !subject.trim() || !body.trim() || !!emailAction}
              className="flex items-center gap-2 px-5 py-2.5 bg-cyan-500/20 text-cyan-400 rounded-lg font-medium text-sm disabled:opacity-30 border border-cyan-500/20 hover:bg-cyan-500/30 transition-all">
              <Mail className="w-4 h-4" />Send as Email
            </button>
            <button onClick={handleSend} disabled={sending || !subject.trim() || !body.trim()}
              className="flex items-center gap-2 px-5 py-2.5 bg-gradient-to-r from-violet-600 to-cyan-500 text-white rounded-lg font-medium text-sm disabled:opacity-30">
              <Send className="w-4 h-4" />{sending ? 'Sending...' : 'In-App Broadcast'}
            </button>
          </div>
        </div>
        {result && <p className="text-green-400 text-sm">{result}</p>}
      </div>

      <div className="bg-[#0E1830] border border-white/10 rounded-xl p-6">
        <h2 className="text-lg font-semibold text-white mb-4">Broadcast History</h2>
        {history.length === 0 ? <p className="text-white/30 text-sm">No broadcasts sent yet</p> : (
          <div className="space-y-3">
            {history.map(b => (
              <div key={b.id} className="flex items-center justify-between py-3 border-b border-white/5">
                <div>
                  <div className="text-white text-sm font-medium">{b.subject}</div>
                  <div className="text-white/30 text-xs">{b.sent_to_segment} — {b.sent_at?.slice(0, 10)}</div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-3 h-3 text-white/30" />
                  <span className="text-white/50 text-sm">{b.recipient_count} recipients</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
